import * as React from "react";

type Props = {
  /** 1..5, fractional values supported (e.g. 4.5 = 4 full + half). */
  value: number;
  /** Append the kit "N / 5" readout. */
  readout?: boolean;
};

export const STAR_PATH =
  "M12 2l2.9 6.95 7.5.6-5.7 4.92 1.74 7.33L12 17.77l-6.44 3.93 1.74-7.33L1.6 9.55l7.5-.6L12 2z";

function Star({ fill }: { fill: "full" | "half" | "empty" }) {
  if (fill === "full") {
    return (
      <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
        <path d={STAR_PATH} fill="currentColor" />
      </svg>
    );
  }
  if (fill === "empty") {
    return (
      <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
        <path
          d={STAR_PATH}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <defs>
        <linearGradient id="half-star" x1="0" x2="1" y1="0" y2="0">
          <stop offset="50%" stopColor="currentColor" />
          <stop offset="50%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={STAR_PATH} fill="url(#half-star)" />
      <path
        d={STAR_PATH}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
      />
    </svg>
  );
}

export function StarRating({ value, readout }: Props) {
  const clamped = Math.max(0, Math.min(5, value));
  const stars: Array<"full" | "half" | "empty"> = [];
  for (let i = 1; i <= 5; i++) {
    if (clamped >= i) stars.push("full");
    else if (clamped >= i - 0.5) stars.push("half");
    else stars.push("empty");
  }
  return (
    <div
      className="t-stars-gold inline-flex items-center gap-1"
      role="img"
      aria-label={`${clamped} out of 5 stars`}
    >
      {stars.map((kind, i) => (
        <span key={i} className={kind === "empty" ? "text-primary/15" : undefined}>
          <Star fill={kind} />
        </span>
      ))}
      {readout ? (
        <span className="ml-3 text-micro font-medium uppercase tracking-link text-primary/60 [font-variant-numeric:tabular-nums]">
          {clamped} / 5
        </span>
      ) : null}
    </div>
  );
}
